const parseIntegerId = (value) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : null;
};

const parseFeatureFlag = (value) => {
    if (typeof value === "boolean") {
        return value;
    }

    if (typeof value === "number") {
        return value === 1;
    }

    if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (["1", "true", "yes", "on", "t", "y"].includes(normalized)) {
            return true;
        }
        if (["0", "false", "no", "off", "", "f", "n"].includes(normalized)) {
            return false;
        }
    }

    return null;
};

export const getters = {
    restaurants(state, getters, rootState) {
        return state.restaurants;
    },
    findRestaurantById(state, getters, rootState) {
        return (id) => {
            return state.restaurants.find((restaurant) => {
                return restaurant.id == id;
            });
        };
    },
    getRestaurantsByService(state, getters, rootState) {
        return (service) => {
            const subscriptions =
                typeof rootState.users.ownerSubscriptions === "object"
                    ? Object.values(rootState.users.ownerSubscriptions)
                    : rootState.users.ownerSubscriptions;
            if (!subscriptions) return [];
            const restaurantsId = subscriptions
                .filter((s) => ["active", "trialing", "past_due", "unpaid"].includes(s.status) && s.module?.type === service)
                .map((s) => Number.parseInt(s.restaurant_id ?? s.restaurant?.id, 10))
                .filter((id) => Number.isFinite(id));

            return state.restaurants.filter((r) => restaurantsId.includes(Number.parseInt(r.id, 10)));
        };
    },
    getOrderingTableRestaurants(state, getters, rootState) {
        return () => {
            const subscriptions =
                typeof rootState.users.ownerSubscriptions === "object"
                    ? Object.values(rootState.users.ownerSubscriptions)
                    : rootState.users.ownerSubscriptions || [];

            const userRestaurantIds = Array.isArray(rootState.users.restaurant_ids)
                ? rootState.users.restaurant_ids
                : Array.isArray(rootState.users.restaurantIds)
                ? rootState.users.restaurantIds
                : [];

            const normalizedUserRestaurantIds = userRestaurantIds.map((id) => parseIntegerId(id)).filter((id) => Number.isFinite(id));

            const orderingModuleTypes = ["click-and-collect", "ordering-table"];
            const hasValidSubscriptionStatus = (status) => ["active", "trialing", "past_due", "unpaid"].includes(status);

            const subscribedOrderingRestaurantIds = subscriptions
                .filter(
                    (subscription) => hasValidSubscriptionStatus(subscription?.status) && orderingModuleTypes.includes(subscription?.module?.type)
                )
                .map((subscription) => parseIntegerId(subscription?.restaurant_id ?? subscription?.restaurant?.id))
                .filter((id) => Number.isFinite(id));

            const featureByRestaurantId = subscriptions.reduce((acc, subscription) => {
                if (!hasValidSubscriptionStatus(subscription?.status) || !orderingModuleTypes.includes(subscription?.module?.type)) {
                    return acc;
                }

                const restaurantId = parseIntegerId(subscription?.restaurant_id ?? subscription?.restaurant?.id);
                if (!Number.isFinite(restaurantId)) {
                    return acc;
                }

                const featureValue = parseFeatureFlag(subscription?.restaurant?.feat_ordering_table);
                if (typeof featureValue === "boolean") {
                    acc[restaurantId] = featureValue;
                }

                return acc;
            }, {});

            return state.restaurants
                .filter((restaurant) => {
                    if (normalizedUserRestaurantIds.length === 0) {
                        return true;
                    }

                    return normalizedUserRestaurantIds.includes(parseIntegerId(restaurant.id));
                })
                .filter((restaurant) => {
                    if (subscribedOrderingRestaurantIds.length === 0) {
                        return true;
                    }

                    return subscribedOrderingRestaurantIds.includes(parseIntegerId(restaurant?.id));
                })
                .filter((restaurant) => {
                    const restaurantFeature = parseFeatureFlag(restaurant?.feat_ordering_table);
                    if (typeof restaurantFeature === "boolean") {
                        return restaurantFeature;
                    }

                    const restaurantId = parseIntegerId(restaurant?.id);
                    if (!Number.isFinite(restaurantId)) {
                        return false;
                    }

                    if (typeof featureByRestaurantId[restaurantId] === "boolean") {
                        return featureByRestaurantId[restaurantId] === true;
                    }

                    // Fallback for legacy payloads where feat_ordering_table is missing.
                    return true;
                });
        };
    },
    getCGV(state, getters, rootState) {
        return (restaurant_id) => {
            const restaurant = state.restaurants.find((restaurant) => {
                return restaurant.id == restaurant_id;
            });

            return typeof restaurant !== "undefined" ? restaurant.cgv : null;
        };
    },
    hasModule(state, getters) {
        return (module, restaurant_id) => getters.getRestaurantsByService(module).some((r) => r.id == restaurant_id);
    },
    isTvaDisabled(state) {
        return (restaurant_id) => {
            const restaurant = state.restaurants.find((restaurant) => restaurant.id == restaurant_id);

            return typeof restaurant !== "undefined" ? restaurant.tva_disabled : false;
        };
    },
    currency(state) {
        return (restaurant_id) => {
            const restaurant = state.restaurants.find((restaurant) => restaurant.id == restaurant_id);

            return typeof restaurant !== "undefined" ? restaurant.currency : "EUR";
        };
    },
    unsavedNotificationsChanges(state) {
        return state.unsavedNotificationsChanges;
    },
    lexicon(state) {
        return (restaurant_id) => {
            const restaurant = state.restaurants.find((restaurant) => restaurant.id == restaurant_id);

            return typeof restaurant !== "undefined" ? restaurant.lexicon : "restaurant";
        };
    },
};

export default getters;
